import { useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("error") === "unauthorised") {
      toast.error("This email is not authorised. Ask your studio admin to add you.");
    }
  }, [params]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAF9F6]">
      {/* Left: sign in */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-sm">
          <div className="mb-16 flex items-center gap-4">
            <img src="/dejavu-mark.png?v=3" alt="Dejavu Weddings" className="w-16 h-16 object-contain shrink-0" />
            <div>
              <div className="font-serif text-5xl md:text-6xl tracking-tight text-[#2A2421] leading-none">Dejavu</div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#5C5652] mt-3">Weddings</div>
            </div>
          </div>

          <h1 className="font-serif text-3xl md:text-4xl tracking-tight text-[#2A2421] mb-2">Welcome back</h1>
          <p className="text-sm text-[#5C5652] mb-10">
            Sign in with your Google account to view your events, trials and expenses.
          </p>

          <button
            type="button"
            onClick={signInWithGoogle}
            data-testid="google-signin-button"
            className="w-full flex items-center justify-center gap-3 border border-[#EAE7E0] bg-white py-3 px-4 text-sm tracking-wide text-[#2A2421] hover:border-[#C88270] hover:shadow-sm transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
            </svg>
            Sign in with Google
          </button>

          <p className="text-xs text-[#B8B2AA] mt-6 leading-relaxed">
            Access is invite-only. If you are a team member, your studio admin must add your Google email first.
          </p>
        </div>
      </div>

      {/* Right: editorial image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(42,36,33,0.20), rgba(42,36,33,0.35)), url('https://customer-assets.emergentagent.com/job_00b0d102-6c35-4522-aafb-40632152e9f9/artifacts/uab26m06_Snapinst.app_455274814_1190791518915899_2643384722648995976_n_1080.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-12 left-12 right-12">
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/90 mb-3">Dejavu Weddings</div>
          <div className="font-serif text-4xl leading-tight text-white max-w-md drop-shadow">
            Where every wedding is quietly, elegantly staged.
          </div>
        </div>
      </div>
    </div>
  );
}
